import { useCallback, useEffect, useMemo, useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { SubscriptionIntervals, SubscriptionPlans } from "@prisma/client";
import clsx from "clsx";

import Button from "@musicfy/components/Button";
import Input from "@musicfy/components/Input";
import PageLoader from "@musicfy/components/PageLoader";
import { MODEL_TRAINING_MAX_QUOTA } from "@musicfy/contants/Features";
import {
  FOUNDING_FIRST_YEAR_DISPLAY,
  FOUNDING_PROMO_CODE,
  FOUNDING_RENEWAL_DISPLAY,
  isFoundingDiscountApplied,
} from "@musicfy/contants/FoundingOffer";
import {
  SALE_CHECKOUT_BADGE_LABEL,
  SALE_EXTENSION_SUBTEXT,
} from "@musicfy/contants/Sale";
import {
  AdditionalVoicesId,
  PriceId,
  ProductId,
  type TAdditionalVoicesId,
  type TPriceId,
  getFormattedPrice,
} from "@musicfy/contants/Subscriptions";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { usePaddleCheckout } from "@musicfy/libs/PaddleProvider/hooks";
import { formatPrice } from "@musicfy/utils";

import PaddleWrapper from "./shared/PaddleWrapper";

/**
 * "Was" prices for the sale strikethrough comparison.
 * Marketing-chosen anchors that show 60% off the live Paddle prices.
 * Keep in sync with MemorialDayPlanCard.tsx.
 */
const MEMORIAL_YEARLY_STRIKETHROUGH: Record<string, number> = {
  starter: 190,
  pro: 590,
  studio: 1200,
};
const MEMORIAL_MONTHLY_STRIKETHROUGH: Record<string, number> = {
  starter: 24.99,
  pro: 62.49,
  studio: 174.99,
};

/** Drop trailing ".00" so whole-dollar prices read "$4" not "$4.00". */
const trimWholeUnit = (formatted: string): string =>
  formatted.replace(/[.,]00(?=\D*$)/, "");

const AdditionalVoices = (): JSX.Element | null => {
  const {
    checkoutContext: { checkoutData },
  } = usePaddleContext();
  const { updateNumberOfAdditionalVoices } = usePaddleCheckout();

  const [localNumberOfAdditionalVoices, setLocalNumberOfAdditionalVoices] =
    useState(0);

  const isEligibleForAdditionalVoices = checkoutData?.items.some(
    (item) => item.product.id === ProductId.label
  );

  const additiontalVoicesLineItem = checkoutData?.items.find((item) =>
    Object.values(AdditionalVoicesId).includes(
      item.price_id as TAdditionalVoicesId
    )
  );

  useEffect(() => {
    setLocalNumberOfAdditionalVoices(additiontalVoicesLineItem?.quantity ?? 0);
  }, [additiontalVoicesLineItem]);

  if (!isEligibleForAdditionalVoices) {
    return null;
  }

  const totalNumberOfCustomVoices =
    MODEL_TRAINING_MAX_QUOTA[SubscriptionPlans.label] +
    localNumberOfAdditionalVoices;

  return (
    <div className="w-full py-4 border-b border-solid border-gray-400">
      <div className="flex items-center justify-between">
        <div className="text-14 text-white/90">Additional Voices</div>
        <div className="flex items-center gap-2">
          <div
            role="button"
            className="text-20"
            onClick={() => {
              if (!additiontalVoicesLineItem) {
                return;
              }
              updateNumberOfAdditionalVoices(
                additiontalVoicesLineItem.quantity - 1
              );
            }}
          >
            -
          </div>
          <Input
            containerClassName="!max-w-16 flex !py-1 !px-0"
            className="text-center !text-14"
            value={localNumberOfAdditionalVoices}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              setLocalNumberOfAdditionalVoices(isNaN(value) ? 0 : value);
            }}
            onBlur={(e) => {
              const value = isNaN(parseInt(e.target.value))
                ? 0
                : parseInt(e.target.value);
              if (
                value === additiontalVoicesLineItem?.quantity ||
                (value === 0 && !additiontalVoicesLineItem)
              ) {
                return;
              }
              updateNumberOfAdditionalVoices(value);
            }}
            onSubmit={() => {
              const value = isNaN(localNumberOfAdditionalVoices)
                ? 0
                : localNumberOfAdditionalVoices + 1;
              if (
                value === additiontalVoicesLineItem?.quantity ||
                (value === 0 && !additiontalVoicesLineItem)
              ) {
                return;
              }
              updateNumberOfAdditionalVoices(value);
            }}
          />
          <div
            role="button"
            className="text-20"
            onClick={() => {
              const value = (additiontalVoicesLineItem?.quantity ?? 0) + 1;
              updateNumberOfAdditionalVoices(value);
            }}
          >
            +
          </div>
        </div>
      </div>
      <div className="flex font-bold text-16 items-center justify-between mt-3">
        <div>Total # of Custom Voices</div>
        <div>{totalNumberOfCustomVoices}</div>
      </div>
    </div>
  );
};

const PromoCode = (): JSX.Element | null => {
  const {
    checkoutContext: { checkoutData, checkoutError },
  } = usePaddleContext();
  const { applyDiscountCode, removeDiscountCode } = usePaddleCheckout();

  const [shouldAddPromoCode, setShouldAddPromoCode] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [isUpdatingDiscountCode, setIsUpdatingDiscountCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = useCallback(async () => {
    const formattedDiscountCode = discountCode.trim().toUpperCase();
    if (!formattedDiscountCode || !!errorMessage) {
      return;
    }
    setIsUpdatingDiscountCode(true);
    const { success } = await applyDiscountCode(formattedDiscountCode);

    if (!success) {
      setErrorMessage("Invalid promo code");
      setIsUpdatingDiscountCode(false);
    }
  }, [applyDiscountCode, discountCode, errorMessage]);

  const handleRemoveDiscountCode = useCallback(() => {
    setIsUpdatingDiscountCode(true);
    removeDiscountCode();
  }, [removeDiscountCode]);

  useEffect(() => {
    if (isUpdatingDiscountCode && !!checkoutData) {
      setIsUpdatingDiscountCode(false);
      return;
    }

    if (checkoutError && isUpdatingDiscountCode) {
      setIsUpdatingDiscountCode(false);
      setErrorMessage("Invalid promo code");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutData, checkoutError]);

  if (!checkoutData) {
    return null;
  }

  const formattedDiscount = formatPrice(
    checkoutData.totals.discount,
    checkoutData.currency_code
  );

  if (!!checkoutData.discount?.code) {
    const isFounding = isFoundingDiscountApplied(checkoutData.discount);
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-light text-gray-400 text-12">Promo Code</div>
            <div className="font-bold text-14 mt-1">
              {checkoutData.discount.code}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              style="solid"
              variant="text"
              onClick={handleRemoveDiscountCode}
              loading={isUpdatingDiscountCode}
              preIcon={<DeleteOutlineIcon className="text-white" />}
            >
              <div className="font-bold pt-1 text-white">
                {formattedDiscount}
              </div>
            </Button>
          </div>
        </div>
        {isFounding && (
          <div className="text-12 font-semibold text-green">
            ✓ {FOUNDING_PROMO_CODE} applied — six months free
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        onClick={() => setShouldAddPromoCode(true)}
        className={clsx(
          "!text-white underline text-14 !absolute pointer-events-none",
          {
            "opacity-0 ml-4": shouldAddPromoCode,
            "opacity-100 ml-0": !shouldAddPromoCode,
          }
        )}
        variant="text"
        style="solid"
      >
        Add Promo Code
      </Button>
      <div
        className={clsx("transition-all", {
          "opacity-0": !shouldAddPromoCode,
          "opacity-100": shouldAddPromoCode,
        })}
      >
        <Input
          onClick={() => {
            setShouldAddPromoCode(true);
          }}
          autoFocus
          value={discountCode}
          onChange={(e) => {
            setDiscountCode(e.target.value.toLocaleUpperCase());
            setErrorMessage("");
          }}
          errorMessage={errorMessage}
          onBlur={(e) => {
            if (e.target.value.trim().length === 0) {
              setShouldAddPromoCode(false);
            }
          }}
          onSubmit={handleSubmit}
          placeholder="Add promotion code"
          className={clsx("!text-14 py-1", {
            "cursor-pointer": !shouldAddPromoCode,
          })}
          errorClassName="text-12"
          postIcon={
            !!discountCode &&
            !errorMessage && (
              <Button
                loading={isUpdatingDiscountCode}
                variant="text"
                style="solid"
                className="!min-h-min !text-white"
                onClick={handleSubmit}
              >
                Apply
              </Button>
            )
          }
        />
      </div>
    </div>
  );
};

/**
 * Reverse-look up the plan slug + interval from a Paddle price_id.
 * PriceId is keyed as `${plan}_${interval}` so we can split on the first
 * underscore once we find a matching entry.
 */
const findPlanFromPriceId = (
  priceId: string
): { planSlug: string; intervalSlug: string } | null => {
  const entry = Object.entries(PriceId).find(([, value]) => value === priceId);
  if (!entry) return null;
  const [key] = entry;
  const splitIndex = key.indexOf("_");
  return {
    planSlug: key.slice(0, splitIndex),
    intervalSlug: key.slice(splitIndex + 1),
  };
};

const CheckoutPreview = (): JSX.Element | null => {
  const {
    checkoutContext: { checkoutData, isCheckoutUpdating },
    pricesContext: { pricesData },
  } = usePaddleContext();

  const planDetails = useMemo(() => {
    if (!checkoutData) return null;

    const { items, currency_code: currencyCode } = checkoutData;

    const planLineItem = items.find((item) =>
      Object.values(PriceId).includes(item.price_id as TPriceId)
    );
    if (!planLineItem) return null;

    const planInfo = findPlanFromPriceId(planLineItem.price_id);
    if (!planInfo) return null;

    const { planSlug, intervalSlug } = planInfo;
    const isYearly = intervalSlug === SubscriptionIntervals.year;

    const monthlyPriceId = PriceId[
      `${planSlug}_${SubscriptionIntervals.month}` as keyof typeof PriceId
    ] as TPriceId | undefined;
    const yearlyPriceId = PriceId[
      `${planSlug}_${SubscriptionIntervals.year}` as keyof typeof PriceId
    ] as TPriceId | undefined;

    const activePerMonth =
      isYearly && yearlyPriceId
        ? getFormattedPrice(
            yearlyPriceId,
            pricesData,
            SubscriptionIntervals.month
          )
        : monthlyPriceId
          ? getFormattedPrice(
              monthlyPriceId,
              pricesData,
              SubscriptionIntervals.month
            )
          : null;

    const yearlyTotal = yearlyPriceId
      ? getFormattedPrice(yearlyPriceId, pricesData, SubscriptionIntervals.year)
      : null;

    if (!activePerMonth) return null;

    const yearlyStrikethroughFloat =
      MEMORIAL_YEARLY_STRIKETHROUGH[planSlug] ?? 0;
    const monthlyStrikethroughFloat =
      MEMORIAL_MONTHLY_STRIKETHROUGH[planSlug] ?? 0;

    const activeStrikethroughFloat = isYearly
      ? yearlyStrikethroughFloat
      : monthlyStrikethroughFloat;
    const activeStrikethroughFormatted = trimWholeUnit(
      formatPrice(activeStrikethroughFloat, currencyCode)
    );

    const activeFloatForCompare =
      isYearly && yearlyTotal
        ? yearlyTotal.floatPrice
        : activePerMonth.floatPrice;

    return {
      planName: planLineItem.product.name ?? "Musicfy Pro",
      isYearly,
      activeStrikethroughFormatted,
      strikethroughUnit: isYearly ? "/year" : "/month",
      showStrikethrough:
        activeStrikethroughFloat > 0 &&
        activeStrikethroughFloat > activeFloatForCompare,
      activePerMonthFormatted: trimWholeUnit(activePerMonth.price),
      yearlyTotalFormatted: yearlyTotal ? trimWholeUnit(yearlyTotal.price) : "",
    };
  }, [checkoutData, pricesData]);

  if (!checkoutData || !planDetails) {
    return null;
  }

  const {
    planName,
    isYearly,
    activeStrikethroughFormatted,
    strikethroughUnit,
    activePerMonthFormatted,
    yearlyTotalFormatted,
    showStrikethrough,
  } = planDetails;

  // Only when Paddle actually attached WELCOME50 (email deep link / manual entry).
  const foundingApplied = isFoundingDiscountApplied(checkoutData.discount);

  return (
    <div
      className={clsx("w-full", {
        "pointer-events-none": isCheckoutUpdating,
      })}
    >
      <div className="mb-3 space-y-1">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-gradient-to-r from-red-dark via-red to-blue px-3 py-1 text-10 font-bold uppercase tracking-wider text-white">
          <span>{SALE_CHECKOUT_BADGE_LABEL}</span>
        </div>
        <div className="text-11 italic text-white/70">
          {SALE_EXTENSION_SUBTEXT}
        </div>
      </div>

      <div className="text-12 font-bold text-white/50 uppercase tracking-widest mb-2">
        Order Summary
      </div>

      <div className="text-22 font-bold leading-tight">{planName}</div>
      <div className="text-13 text-white/60 mb-4">
        {isYearly
          ? "Annual subscription · locked rate"
          : "Monthly subscription"}
      </div>

      <div className="rounded-12 border border-pink-200/25 bg-pink-200/[0.04] p-4 mb-4">
        {foundingApplied ? (
          <>
            {showStrikethrough && (
              <div className="text-13 text-white/40 line-through">
                {activeStrikethroughFormatted}
                {strikethroughUnit}
              </div>
            )}
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-38 font-bold leading-none">
                {FOUNDING_FIRST_YEAR_DISPLAY}
              </span>
              <span className="text-14 text-white/70">first year</span>
            </div>
            <div className="mt-1 text-13 text-white/70">
              Then {FOUNDING_RENEWAL_DISPLAY}/year · locked for life
            </div>
            <div className="mt-1.5 text-13 font-semibold text-green">
              ✓ {FOUNDING_PROMO_CODE} applied — six months free
            </div>
          </>
        ) : (
          <>
            {showStrikethrough && (
              <div className="text-13 text-white/40 line-through">
                {activeStrikethroughFormatted}
                {strikethroughUnit}
              </div>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-38 font-bold leading-none">
                {activePerMonthFormatted}
              </span>
              <span className="text-16 text-pink-200">/month</span>
            </div>
            <div className="mt-1 text-13 text-white/70">
              {isYearly
                ? `Billed annually · ${yearlyTotalFormatted}/year`
                : "Billed monthly"}
            </div>
          </>
        )}
      </div>

      <div className="space-y-1 text-12 text-white/60 mb-2">
        <div>✓ Locked for the life of your account</div>
        <div>✓ Cancel anytime · voice library preserved</div>
        {isYearly && <div>✓ 7-day no-questions refund</div>}
      </div>

      <div className="mt-4 pt-3 border-t border-white/10 space-y-3">
        <AdditionalVoices />
        <PromoCode />
      </div>
    </div>
  );
};

const Checkout = (): JSX.Element => {
  const {
    checkoutContext: { checkoutData, isCheckoutUpdating },
  } = usePaddleContext();

  const { closeCheckout } = usePaddleCheckout();

  return (
    <PaddleWrapper open onClose={closeCheckout} isLoading={!checkoutData}>
      {isCheckoutUpdating && (
        <div className="absolute rounded-12 z-10 w-full h-full inset-0 bg-black/50">
          <PageLoader />
        </div>
      )}
      <CheckoutPreview />
    </PaddleWrapper>
  );
};

export default Checkout;
